import { useState, useEffect } from 'preact/hooks';
import { apiGet, apiPost } from '../lib/api';
import { OtpDialog } from '../components/OtpDialog';

interface Service {
  id: string;
  name: string;
  group_name: string | null;
  is_restartable: number;
}

interface Server {
  id: string;
  name: string;
  hostname: string;
  services: Service[];
}

interface OtpRequest {
  requestId: string;
  expiresAt: string;
  service: string;
  server: string;
}

interface RestartStatus {
  id: string;
  status: 'pending' | 'executing' | 'completed' | 'failed';
  result: string | null;
  serviceName: string;
  serverName: string;
}

type PageState = 'select' | 'otp' | 'executing' | 'result';

export function Restart() {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedServer, setSelectedServer] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');
  const [pageState, setPageState] = useState<PageState>('select');
  const [otpRequest, setOtpRequest] = useState<OtpRequest | null>(null);
  const [otpError, setOtpError] = useState('');
  const [restartStatus, setRestartStatus] = useState<RestartStatus | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadServers();
  }, []);

  async function loadServers() {
    try {
      const data = await apiGet<Server[]>('/config/servers');
      setServers(data);
    } catch (err) {
      setError('Server konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  const currentServer = servers.find((s) => s.id === selectedServer);
  const restartableServices = currentServer?.services.filter((s) => s.is_restartable) ?? [];

  async function handleRequestOtp() {
    if (!selectedServer || !selectedService) return;
    setError('');
    try {
      const data = await apiPost<OtpRequest>('/restart/request', {
        serverId: selectedServer,
        serviceId: selectedService,
      });
      setOtpRequest(data);
      setOtpError('');
      setPageState('otp');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      if (msg.includes('429')) {
        setError('Zu viele Fehlversuche. Bitte 30 Min warten.');
      } else {
        setError('OTP-Anfrage fehlgeschlagen: ' + msg);
      }
    }
  }

  async function handleConfirmOtp(code: string) {
    if (!otpRequest) return;
    setOtpError('');
    try {
      const data = await apiPost<{ restartId: string; status: string; message: string }>(
        '/restart/confirm',
        { requestId: otpRequest.requestId, code }
      );
      setPageState('executing');
      pollRestartStatus(data.restartId);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('403')) {
        setOtpError('Falscher oder abgelaufener Code.');
      } else {
        setOtpError('Bestätigung fehlgeschlagen.');
      }
    }
  }

  async function handleResendOtp() {
    if (!selectedServer || !selectedService) return;
    try {
      const data = await apiPost<OtpRequest>('/restart/request', {
        serverId: selectedServer,
        serviceId: selectedService,
      });
      setOtpRequest(data);
      setOtpError('');
    } catch {
      setOtpError('Code konnte nicht erneut gesendet werden.');
    }
  }

  function pollRestartStatus(restartId: string) {
    const poll = async () => {
      try {
        const data = await apiGet<RestartStatus>(`/restart/status?id=${restartId}`);
        setRestartStatus(data);
        if (data.status === 'completed' || data.status === 'failed') {
          setPageState('result');
          return;
        }
        // Continue polling
        setTimeout(poll, 3000);
      } catch {
        setRestartStatus({
          id: restartId,
          status: 'failed',
          result: 'Status konnte nicht abgefragt werden.',
          serviceName: otpRequest?.service ?? '',
          serverName: otpRequest?.server ?? '',
        });
        setPageState('result');
      }
    };
    poll();
  }

  function handleReset() {
    setPageState('select');
    setOtpRequest(null);
    setOtpError('');
    setRestartStatus(null);
    setSelectedService('');
    setError('');
  }

  if (loading) {
    return (
      <div class="page">
        <h1>Service Restart</h1>
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div class="page">
      <h1>Service Restart</h1>

      {pageState === 'select' && (
        <div class="restart-form">
          {error && <p class="error">{error}</p>}

          <div class="form-group">
            <label class="form-label" htmlFor="server-select">Server</label>
            <select
              id="server-select"
              class="form-select"
              value={selectedServer}
              onChange={(e) => {
                setSelectedServer((e.target as HTMLSelectElement).value);
                setSelectedService('');
              }}
            >
              <option value="">Server wählen...</option>
              {servers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {selectedServer && (
            <div class="form-group">
              <label class="form-label" htmlFor="service-select">Service</label>
              {restartableServices.length === 0 ? (
                <p class="hint">Keine neustartbaren Services auf diesem Server.</p>
              ) : (
                <select
                  id="service-select"
                  class="form-select"
                  value={selectedService}
                  onChange={(e) => setSelectedService((e.target as HTMLSelectElement).value)}
                >
                  <option value="">Service wählen...</option>
                  {restartableServices.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.group_name ? `(${s.group_name})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {selectedService && (
            <button class="btn btn-danger" onClick={handleRequestOtp}>
              🔄 Restart anfordern
            </button>
          )}
        </div>
      )}

      {pageState === 'otp' && otpRequest && (
        <OtpDialog
          requestId={otpRequest.requestId}
          serviceName={otpRequest.service}
          serverName={otpRequest.server}
          expiresAt={otpRequest.expiresAt}
          onConfirm={handleConfirmOtp}
          onClose={() => setPageState('select')}
          onResend={handleResendOtp}
          error={otpError}
        />
      )}

      {pageState === 'executing' && (
        <div class="restart-executing">
          <div class="spinner" />
          <h2>Restart wird ausgeführt...</h2>
          <p>
            {otpRequest?.service} auf {otpRequest?.server}
          </p>
          <p class="hint">Dies kann einige Sekunden dauern.</p>
        </div>
      )}

      {pageState === 'result' && restartStatus && (
        <div class={`restart-result ${restartStatus.status === 'completed' ? 'success' : 'failure'}`}>
          <div class="result-icon">
            {restartStatus.status === 'completed' ? '✅' : '❌'}
          </div>
          <h2>
            {restartStatus.status === 'completed' ? 'Restart erfolgreich' : 'Restart fehlgeschlagen'}
          </h2>
          <p class="result-detail">
            {restartStatus.serviceName} auf {restartStatus.serverName}
          </p>
          {restartStatus.result && (
            <pre class="result-output">{restartStatus.result}</pre>
          )}
          <button class="btn btn-primary" onClick={handleReset}>
            Zurück
          </button>
        </div>
      )}
    </div>
  );
}
